// utils/listingCompletionHelper.ts
// Helper utilities for tracking listing completion across all steps

export interface StepCompletionStatus {
  [stepNumber: number]: {
    completed: boolean;
    fieldsCompleted?: number;
    totalFields?: number;
  };
}

/**
 * Calculate completion status for a step based on form values and required fields
 */
export const calculateStepCompletion = (
  formValues: Record<string, any>,
  requiredFields: string[],
  hasErrors: boolean = false
): { completed: boolean; fieldsCompleted: number; totalFields: number } => {
  // Count how many required fields are filled
  const filledFields = requiredFields.filter((field) => {
    const value = formValues[field];
    return value !== "" && value !== null && value !== undefined && value !== 0;
  }).length;

  // Step is complete when all fields filled AND no validation errors
  const completed = filledFields === requiredFields.length && !hasErrors;

  return {
    completed,
    fieldsCompleted: filledFields,
    totalFields: requiredFields.length,
  };
};

/**
 * Check if property data indicates a step is already completed
 * Based on PropertyToEdit interface
 */
export const checkExistingCompletion = (
  propertyData: any
): StepCompletionStatus => {
  const completionStatus: StepCompletionStatus = {
    1: { completed: false },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false },
    9: { completed: false },
  };

  if (!propertyData) return completionStatus;

  // Step 1: Description - Required fields
  if (
    propertyData.title &&
    propertyData.property_category?.id &&
    propertyData.property_type?.id &&
    propertyData.country &&
    propertyData.guest_number &&
    propertyData.description &&
    propertyData.cancellation_policy?.id
  ) {
    completionStatus[1] = { completed: true };
  }

  // Step 2: Price - Has pricing information
  if (propertyData.price_per_night || propertyData.price_per_month || propertyData.minimum_months_of_booking) {
    completionStatus[2] = { completed: true };
  }

  // Step 3: Images - Has at least one image
  if (propertyData.place_images && propertyData.place_images.length > 0) {
    completionStatus[3] = { completed: true };
  }

  // Step 4: Details - Has property details
  if (
    propertyData.total_bedrooms !== null &&
    propertyData.total_bedrooms !== undefined &&
    propertyData.total_bathrooms !== null &&
    propertyData.total_bathrooms !== undefined
  ) {
    completionStatus[4] = { completed: true };
  }

  // Step 5: Location - Has coordinates or full address
  if (
    (propertyData.latitude && propertyData.longitude) ||
    (propertyData.address && propertyData.city && propertyData.state)
  ) {
    completionStatus[5] = { completed: true };
  }

  // Step 6: Amenities - Has amenities selected
  if (propertyData.amenities && propertyData.amenities.length > 0) {
    completionStatus[6] = { completed: true };
  }

  // Step 7: Calendar - Has availability set (either both dates or just start date with unlimited)
  if (
    propertyData.availability_start &&
    (propertyData.availability_end || propertyData.unlimited_availability)
  ) {
    completionStatus[7] = { completed: true };
  }

  // Step 8: Rooms - Has rooms configured
  if (propertyData.rooms && propertyData.rooms.length > 0) {
    completionStatus[8] = { completed: true };
  }

  // Step 9: Preview - Complete when all previous steps are done OR property has been submitted
  const allPreviousStepsComplete = Object.entries(completionStatus)
    .filter(([step]) => Number(step) < 9)
    .every(([_, status]) => status.completed);

  completionStatus[9] = {
    completed:
      allPreviousStepsComplete ||
      (propertyData.property_state !== "draft" &&
        propertyData.property_state !== undefined),
  };

  return completionStatus;
};

/**
 * Get the overall completion percentage
 */
export const getOverallCompletion = (
  stepCompletionStatus: StepCompletionStatus
): number => {
  const totalSteps = Object.keys(stepCompletionStatus).length;
  const completedSteps = Object.values(stepCompletionStatus).filter(
    (status) => status.completed
  ).length;

  return Math.round((completedSteps / totalSteps) * 100);
};

/**
 * Save completion status to localStorage
 */
export const saveCompletionToStorage = (
  propertyId: string,
  stepCompletionStatus: StepCompletionStatus
): void => {
  try {
    localStorage.setItem(
      `listing_completion_${propertyId}`,
      JSON.stringify(stepCompletionStatus)
    );
  } catch (error) {
    console.error("Failed to save completion status:", error);
  }
};

/**
 * Load completion status from localStorage
 */
export const loadCompletionFromStorage = (
  propertyId: string
): StepCompletionStatus | null => {
  try {
    const stored = localStorage.getItem(`listing_completion_${propertyId}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Failed to load completion status:", error);
    return null;
  }
};